import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Token storage in memory (not localStorage for security)
let accessToken: string | null = null;
let refreshToken: string | null = null;

export const setTokens = (access: string, refresh: string) => {
  accessToken = access;
  refreshToken = refresh;
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
};

export const getAccessToken = () => accessToken;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // Keep for cookie support (fallback)
});

// Add Authorization header with access token
api.interceptors.request.use(
  (config) => {
    if (accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-refresh on 401 using refresh token from memory
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const isAuthEndpoint = original.url?.includes('/auth/');
    if (err.response?.status === 401 && !original._retry && !isAuthEndpoint && refreshToken) {
      original._retry = true;
      try {
        // Send refresh token in body
        const response = await api.post('/api/v1/auth/refresh', { refresh_token: refreshToken });
        const { access_token, refresh_token: new_refresh } = response.data;
        setTokens(access_token, new_refresh);
        return api(original);
      } catch {
        clearTokens();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  login: (form: FormData) =>
    api.post('/api/v1/auth/login', form, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }),
  logout: () => api.post('/api/v1/auth/logout'),
  me: () => api.get('/api/v1/auth/me'),
  register: (data: object) => api.post('/api/v1/auth/register', data),
  verifyEmail: (token: string) => api.get(`/api/v1/auth/verify-email/${token}`),
  requestPasswordReset: (email: string) =>
    api.post('/api/v1/auth/password-reset/request', { email }),
  confirmPasswordReset: (token: string, new_password: string) =>
    api.post('/api/v1/auth/password-reset/confirm', { token, new_password }),
  changePassword: (current_password: string, new_password: string) =>
    api.post('/api/v1/auth/password/change', { current_password, new_password }),
  // MFA
  mfaSetup: () => api.post('/api/v1/auth/mfa/setup'),
  mfaEnable: (totp_code: string) => api.post('/api/v1/auth/mfa/enable', { totp_code }),
  mfaDisable: (totp_code: string) => api.delete('/api/v1/auth/mfa', { data: { totp_code } }),
  mfaChallenge: (mfa_token: string, totp_code: string) =>
    api.post('/api/v1/auth/mfa/challenge', { mfa_token, totp_code }),
};

// Organisations
export const organisationsAPI = {
  list: () => api.get('/api/v1/organisations/'),
  get: (id: number) => api.get(`/api/v1/organisations/${id}`),
  create: (data: object) => api.post('/api/v1/organisations/', data),
  update: (id: number, data: object) => api.put(`/api/v1/organisations/${id}`, data),
  getICC: (id: number) => api.get(`/api/v1/organisations/${id}/icc`),
};

// Checklist
export const checklistAPI = {
  industries: () => api.get<string[]>('/api/v1/checklist/industries'),
  taxonomy: () => api.get<{
    industry_taxonomy: Record<string, Record<string, string[]>>;
    workforce_types: string[];
    work_modes: string[];
    org_size_bands: string[];
  }>('/api/v1/checklist/taxonomy'),
  categories: () => api.get('/api/v1/checklist/categories'),
  questions: (params?: {
    category_id?: number;
    industry?: string;
    sub_industry?: string;
    unit_type?: string;
    workforce_type?: string;
    work_mode?: string;
    org_size_band?: string;
    requirement_type?: string;
  }) => api.get('/api/v1/checklist/questions', { params }),
  // Admin
  createCategory: (data: object) => api.post('/api/v1/admin/checklist/categories', data),
  updateCategory: (id: number, data: object) => api.put(`/api/v1/admin/checklist/categories/${id}`, data),
  createQuestion: (data: object) => api.post('/api/v1/admin/checklist/questions', data),
  updateQuestion: (id: number, data: object) => api.put(`/api/v1/admin/checklist/questions/${id}`, data),
  deleteQuestion: (id: number) => api.delete(`/api/v1/admin/checklist/questions/${id}`),
};

// Assessments
export const assessmentsAPI = {
  list: () => api.get('/api/v1/assessments/'),
  get: (id: number) => api.get(`/api/v1/assessments/${id}`),
  create: (data: object) => api.post('/api/v1/assessments/', data),
  saveResponses: (id: number, data: object) => api.post(`/api/v1/assessments/${id}/responses`, data),
  submit: (id: number) => api.post(`/api/v1/assessments/${id}/submit`),
};

// Evidence
export const evidenceAPI = {
  upload: (formData: FormData) =>
    api.post('/api/v1/evidence/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (assessmentId: number, categoryId?: number) =>
    api.get(`/api/v1/evidence/${assessmentId}`, { params: categoryId != null ? { category_id: categoryId } : {} }),
  verify: (id: number) => api.put(`/api/v1/evidence/${id}/verify`),
  delete: (id: number) => api.delete(`/api/v1/evidence/${id}`),
};

// Scoring
export const scoringAPI = {
  compute: (assessmentId: number) => api.post(`/api/v1/scoring/compute/${assessmentId}`),
  breakdown: (assessmentId: number) => api.get(`/api/v1/scoring/${assessmentId}/breakdown`),
  gaps: (assessmentId: number) => api.get(`/api/v1/scoring/${assessmentId}/gaps`),
};

// Dashboard
export const dashboardAPI = {
  summary: () => api.get('/api/v1/dashboard/summary'),
  heatmap: () => api.get('/api/v1/dashboard/heatmap'),
  risks: () => api.get('/api/v1/dashboard/risks'),
  deadlines: () => api.get('/api/v1/dashboard/deadlines'),
  trends: () => api.get('/api/v1/dashboard/trends'),
};

// Reports
export const reportsAPI = {
  generate: (assessmentId: number) =>
    api.post(`/api/v1/reports/generate/${assessmentId}`, {}, { responseType: 'blob' }),
  history: (orgId: number) => api.get(`/api/v1/reports/history/${orgId}`),
};

export default api;
